import { AssortmentStrategy, MerchandisingPlan } from './merchandisingTypes';
import { AutumnTransition, PlanningHorizon, ProposedWorklist } from './seasonalPlanning';
import { StoreInventoryReview } from './storeInventoryReview';

export function readNokeError(error: unknown, fallback: string): string {
  const err = error as { message?: string; response?: { data?: { message?: string; error?: string | { message?: string } } } } | null | undefined;
  const data = err?.response?.data;
  const nested = data?.error && typeof data.error === 'object' ? data.error.message : undefined;
  return [nested, data?.message, typeof data?.error === 'string' ? data.error : undefined, err?.message]
    .find((message): message is string => typeof message === 'string' && message.trim().length > 0) || fallback;
}

export interface RestockLine {
  kind: 'display' | 'dispatch' | 'order' | 'waiting';
  sku: string;
  title: string;
  cat: string;
  vendor: string;
  handle: string | null;
  image_url: string | null;
  source_tag: string | null;
  price: number;
  variant_id: number | null;
  color: string | null;
  size: string | null;
  qty: number;
  target: number;
  on_hand: number;
  office: number;
  transit: number;
  storage_slot: string | null;
  counter_sold: number;
  sku_counter_sold: number;
  online_u14: number;
  forecast_cover: number;
  sold_out_winner: boolean;
  reason: string;
  fixture_label?: string;
  evidence_label?: string;
  evidence_note?: string;
}

export interface RestockRemoval {
  sku: string;
  review_type?: 'excess_depth' | 'recent_inactivity';
  title: string;
  image_url?: string | null;
  on_hand: number;
  reason: string;
  variants: {
    variant_id: number;
    color: string | null;
    size: string | null;
    on_hand: number;
    storage_slot?: string | null;
    suggested_reduction?: number;
  }[];
}

export interface OfficeReturnCandidate {
  sku: string;
  title: string;
  image_url: string | null;
  reason: string;
  status: 'excess_stock' | 'replace_after_review';
  replacement_sku?: string;
  qty: number;
  variants: {
    variant_id: number;
    color: string | null;
    size: string | null;
    on_hand: number;
    keep: number;
    qty: number;
    storage_slot?: string | null;
  }[];
}

export interface DemandHistorySummary {
  windows_days: number[];
  previous_year_available: boolean;
  notes: string[];
}

export interface SeasonalAdvice {
  title: string;
  notes: string[];
}

export interface RestockPlan {
  store_inventory_review?: StoreInventoryReview;
  daily_actions?: { core: DailyAction[]; observation?: DailyAction[]; seasonal_launch?: DailyAction[]; stock_rotation: DailyAction[] };
  planning_horizon?: PlanningHorizon;
  proposed_worklist?: ProposedWorklist;
  merchandising?: MerchandisingPlan;
  office_returns?: OfficeReturnCandidate[];
  meta: {
    autumn_transition?: AutumnTransition;
    strategy?: AssortmentStrategy;
    generated_at: string;
    cover_days: number;
    demand_history?: DemandHistorySummary;
    seasonal_advice?: SeasonalAdvice;
    space: {
      rail_now: number;
      pull: number;
      add: number;
      incoming?: number;
      end_state: number;
      assumed_capacity: number;
      over_by: number;
    };
    selection?: {
      trial_skus: number;
      deferred_pieces: number;
      capacity_source: 'estimate' | 'configured';
      notes: string[];
    };
    weather?: {
      status: 'available' | 'unavailable';
      label: string;
      summary: string;
      generated_at: string | null;
      days: number;
      source: string;
    };
  };
  display?: RestockLine[];
  dispatch: RestockLine[];
  order: RestockLine[];
  pull: RestockRemoval[];
  waiting?: RestockLine[];
  deferred?: { sku: string; title: string; qty: number; reason: string }[];
  review?: RestockRemoval[];
  blocked: { sku: string; title: string; reason: string }[];
  shelf?: { label: string; items: { sku: string }[]; keep: { sku: string }[] }[];
}

export interface DailyAction extends Pick<RestockLine, 'sku' | 'title' | 'image_url' | 'cat' | 'variant_id' | 'color' | 'size' | 'qty' | 'target' | 'on_hand' | 'office' | 'transit' | 'source_tag' | 'reason' | 'evidence_label' | 'evidence_note'> {
  fixture_label: string;
  source: 'store_display' | 'office' | 'transit' | 'supplier' | 'blocked';
  assortment_role: 'core' | 'observation' | 'stock_rotation' | 'seasonal_launch';
  source_policy: 'replenish_core' | 'stock_only' | 'saved_manual_purchase' | 'seasonal_trial';
  needs_space: boolean;
  requires_adoption: boolean;
  executable: boolean;
  purchase_check_required?: boolean;
}

export const variantLabel = (color: string | null, size: string | null): string => {
  const c = color && color !== 'Default Title' ? color : null;
  const s = size && !(color === 'Default Title' && size === 'F') ? size : null;
  return [c, s].filter(Boolean).join(' / ') || '單一規格';
};

export function reviewVariantLabel(variant: RestockRemoval['variants'][number]): string {
  const reduction = Math.min(variant.on_hand, Math.max(0, variant.suggested_reduction || 0));
  return `${variantLabel(variant.color, variant.size)}：現有 ${variant.on_hand} 件${reduction > 0 ? `，建議減量 ${reduction} 件／保留 ${variant.on_hand - reduction} 件` : ''}`;
}

export const pieces = (lines: RestockLine[]): number => lines.reduce((sum, line) => sum + line.qty, 0);

export const officeReturnQuantity = (variant: OfficeReturnCandidate['variants'][number]): number =>
  Math.max(0, Math.min(variant.qty, variant.on_hand - variant.keep));

export const officeReturnPieces = (rows: OfficeReturnCandidate[]): number => rows
  .filter((row) => row.status === 'excess_stock')
  .reduce((total, row) => total + row.variants.reduce((qty, variant) => qty + officeReturnQuantity(variant), 0), 0);

export function officeReturnListText(rows: OfficeReturnCandidate[]): string {
  const output = [`NOKE 建議撤回辦公室清單（${officeReturnPieces(rows)} 件）`,
    '未登記移動／Outlet 未上架。請核對現場後，至庫存頁登記移回辦公室；再評估品況與 Outlet 售價。'];
  rows.filter((row) => row.status === 'excess_stock').forEach((row) => {
    const variants = row.variants.filter((variant) => officeReturnQuantity(variant) > 0);
    if (!variants.length) return;
    output.push('', `${row.sku} ${row.title}`);
    variants.forEach((variant) => output.push(`  ${variantLabel(variant.color, variant.size)}：撤回 ${officeReturnQuantity(variant)} 件，門市保留 ${variant.keep} 件（現有 ${variant.on_hand} 件）${variant.storage_slot ? `｜${variant.storage_slot}` : ''}`));
    output.push(`  原因：${row.reason}`);
  });
  return output.join('\n');
}

export function groupProducts(lines: RestockLine[]): RestockLine[][] {
  const groups = new Map<string, RestockLine[]>();
  lines.forEach((line) => {
    const key = `${line.vendor}:${line.sku}`;
    groups.set(key, [...(groups.get(key) || []), line]);
  });
  return Array.from(groups.values());
}

export function groupOrderLines(lines: RestockLine[]): { key: string; source: string; lines: RestockLine[] }[] {
  const groups = new Map<string, { key: string; source: string; lines: RestockLine[] }>();
  lines.forEach((line) => {
    const source = line.vendor === 'MdoC' ? 'MdoC 倉庫' : line.source_tag?.split('/')[0].trim() || '供應商待確認';
    const key = `${line.vendor}:${source.toLowerCase()}`;
    const group = groups.get(key) || { key, source, lines: [] };
    group.lines.push(line);
    groups.set(key, group);
  });
  return Array.from(groups.values());
}

export function fixtureLabel(line: RestockLine, plan: RestockPlan): string {
  return line.fixture_label || plan.shelf?.find((fixture) =>
    [...fixture.items, ...fixture.keep].some((item) => item.sku === line.sku))?.label || '擺放位置待確認';
}

export function actionListText(plan: RestockPlan, action: 'display' | 'dispatch' | 'order'): string {
  const lines = plan[action] || [];
  const title = { display: '門市上架清單', dispatch: '辦公室調貨清單', order: '待訂貨清單' }[action];
  const date = new Date(plan.meta.generated_at).toLocaleString('zh-TW');
  const output = [`NOKE ${title}（${pieces(lines)} 件）`, `建議產生時間：${date}`];
  if (action === 'order') output.push('僅為建議，尚未下單；下單前核對已訂與在途，避免重複訂貨。');
  if (action === 'dispatch') output.push('此清單尚未登記送出；實際調貨請在庫存頁操作。');
  if (plan.meta.space.pull > 0) output.push(`本方案需先按撤回清單騰出 ${plan.meta.space.pull} 件空間。`);
  const groups = action === 'order' ? groupOrderLines(lines) : [{ key: action, source: '', lines }];
  groups.forEach((group) => {
    if (group.source) output.push('', `■ ${group.source}（${pieces(group.lines)} 件）`);
    groupProducts(group.lines).forEach((product) => {
      output.push(`${product[0].sku} ${product[0].title}`);
      product.forEach((line) => output.push(`  ${variantLabel(line.color, line.size)} × ${line.qty} 件 → ${fixtureLabel(line, plan)}`));
    });
  });
  return output.join('\n');
}

export function removalListText(plan: RestockPlan): string {
  const output = ['NOKE 建議撤回清單（尚未登記移動）'];
  plan.pull.forEach((row) => {
    output.push(`${row.sku} ${row.title}`);
    row.variants.filter((variant) => variant.on_hand > 0).forEach((variant) =>
      output.push(`  ${variantLabel(variant.color, variant.size)} × ${variant.on_hand} 件${variant.storage_slot ? `｜${variant.storage_slot}` : ''}`));
    output.push(`  原因：${row.reason}`);
  });
  return output.join('\n');
}
